'use client'

import Link from 'next/link'
import { ExternalLink } from 'lucide-react'
import { Badge } from '@elmnts/ui/badge'
import { StatusCodeBadge } from '@/components/audit/StatusCodeBadge'
import { useHasPermission } from '@/hooks/usePermissions'
import { cn } from '@/lib/utils'
import type { NotificationItem } from '@/services/notifications/types'
import { Notification } from '../Notification'
import { getKindStyle } from '../kind-style'
import { defaultRenderer } from './default'
import type { NotificationRenderer, NotificationViewProps } from './types'

/** Literal do gate das rotas de `/admin/errors`. Igual ao de `hooks/admin/useErrorsQuery`. */
const ADMIN_LOGS_READ = 'admin:logs:read'

/** Prefixo EXATO da linha que o back acrescenta ao corpo (`admin_http_error.py:45`). */
const PREFIXO_DO_LINK = 'Detalhes completos: /admin/errors/'

/** `type_id` do aviso que vai pro usuário que SOFREU o erro. */
const USER_HTTP_ERROR = 'user_http_error'

/**
 * O detalhe técnico (exceção, rota, status) só sai pro `admin_http_error`.
 *
 * O back decide **deliberadamente** o oposto pro outro destinatário: `user_http_error.title_template`
 * é a string fixa `'Encontramos um erro inesperado'`, com o comentário "Tom intencionalmente leve pra
 * não assustar" (`user_http_error.py:1-4`, `:26`). O `admin_http_error`, esse sim, tem
 * `title_template = 'Erro {status_code}: {exception_type} em {method} {path}'`
 * (`admin_http_error.py:35`) — pra ele o técnico é o conteúdo.
 *
 * Os dois `context` trazem `path` e `exception_type`: **ter o dado não é licença para exibi-lo**.
 */
function ehDetalheTecnico(notification: NotificationItem): boolean {
  return notification.type_id !== USER_HTTP_ERROR
}

/**
 * Remove do corpo a linha "Detalhes completos: /admin/errors/{id}".
 *
 * Deixar a linha **e** o botão é mostrar a mesma informação duas vezes, uma delas como texto morto —
 * que é exatamente o que esta entrega veio consertar.
 *
 * Casa o **prefixo**, nunca "a última linha" por posição: o corpo do `user_http_error` também termina
 * numa linha curta, mas ela é o `error_log_id` cru — o código que a pessoa passa pro suporte
 * (`user_http_error.py:28-34`). Cortá-lo apagaria a única informação acionável daquele aviso.
 */
export function corpoSemLinhaDeLink(body: string): string {
  const linhas = body.split('\n')
  const ultima = linhas[linhas.length - 1] ?? ''
  if (!ultima.startsWith(PREFIXO_DO_LINK)) return body
  // Tira também a linha em branco que separava o bloco anterior.
  while (linhas.length > 1 && (linhas[linhas.length - 1] === '' || linhas[linhas.length - 1].startsWith(PREFIXO_DO_LINK))) {
    linhas.pop()
  }
  return linhas.join('\n')
}

interface ContextoDeErro {
  errorLogId: string
  method: string | null
  path: string | null
  statusCode: number | null
  exceptionType: string | null
}

/**
 * `context` é `dict[str, Any]` no back — nada garante a forma em runtime. Só `error_log_id` é
 * essencial (é o que faz este renderer valer a pena); sem ele, cai no default.
 *
 * Um só leitor serve `admin_http_error` e `user_http_error`: o segundo tem um subconjunto dos campos
 * do primeiro (sem `status_code`, `ip`, `duration_ms`, `exception_message`).
 */
function readContext(notification: NotificationItem): ContextoDeErro | null {
  const ctx = notification.context as Record<string, unknown> | undefined
  const errorLogId = ctx?.error_log_id
  if (typeof errorLogId !== 'string' || errorLogId === '') return null
  return {
    errorLogId,
    method: typeof ctx?.method === 'string' ? ctx.method : null,
    path: typeof ctx?.path === 'string' ? ctx.path : null,
    statusCode: typeof ctx?.status_code === 'number' ? ctx.status_code : null,
    exceptionType: typeof ctx?.exception_type === 'string' ? ctx.exception_type : null,
  }
}

/** Botão pro detalhe — só com a permission. Sem ela: nada, nem botão nem frase de desculpa. */
function BotaoDeDetalhes({ errorLogId }: { errorLogId: string }) {
  const pode = useHasPermission(ADMIN_LOGS_READ)
  if (!pode) return null
  return (
    <Link
      href={`/admin/errors/${errorLogId}`}
      className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent"
    >
      <ExternalLink className="size-4" />
      Ver detalhes completos
    </Link>
  )
}

function Requisicao({ ctx }: { ctx: ContextoDeErro }) {
  if (!ctx.method && !ctx.path) return null
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5 text-xs">
      {ctx.method && <Badge variant="outline" className="font-mono text-[10px]">{ctx.method}</Badge>}
      {ctx.path && <code className="truncate" title={ctx.path}>{ctx.path}</code>}
    </span>
  )
}

function PreviewCard(props: NotificationViewProps) {
  const { notification, href, showReadAction = true, showDeleteAction = true } = props
  const ctx = readContext(notification)
  if (!ctx) return <defaultRenderer.Preview {...props} />
  /* Pro usuário final o card certo é o genérico: título e corpo como o back os escreveu, sem nome de
     exceção nem rota. Não há nada a acrescentar ao que ele já diz. */
  if (!ehDetalheTecnico(notification)) return <defaultRenderer.Preview {...props} />

  const style = getKindStyle(notification.kind)
  const card = (
    <div
      className={cn(
        'w-full min-w-0 overflow-hidden rounded-lg border p-3 transition-all duration-150 hover:shadow-md',
        style.borderClass,
        style.bgClass,
        href && 'cursor-pointer hover:bg-accent/40',
      )}
    >
      <Notification notification={notification}>
        <div className="flex items-start gap-3">
          <Notification.KindIcon className="mt-0.5" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex items-start justify-between gap-2">
              <p className="min-w-0 flex-1 text-sm font-medium leading-tight">
                {ctx.exceptionType ?? notification.title}
              </p>
              {ctx.statusCode != null ? <StatusCodeBadge status={ctx.statusCode} /> : <Notification.KindBadge />}
            </div>
            <Requisicao ctx={ctx} />
            <div className="flex items-center justify-between gap-2">
              <Notification.Time className="text-[11px]" />
              {(showReadAction || showDeleteAction) && (
                <div className="flex gap-1">
                  {showReadAction && <Notification.ReadAction onSuccess={props.onMarkedRead} />}
                  {showDeleteAction && <Notification.DeleteAction onSuccess={props.onDeleted} />}
                </div>
              )}
            </div>
          </div>
        </div>
      </Notification>
    </div>
  )

  return href ? <Link href={href} className="block">{card}</Link> : card
}

function FullView(props: NotificationViewProps) {
  const { notification, showReadAction = true, showDeleteAction = true } = props
  const ctx = readContext(notification)
  if (!ctx) return <defaultRenderer.Full {...props} />
  const tecnico = ehDetalheTecnico(notification)

  return (
    <Notification notification={notification}>
      <article className="space-y-6">
        <header className="space-y-2">
          <div className="flex items-center gap-2">
            <Notification.KindIcon className="size-5" />
            <Notification.KindBadge />
            {tecnico && ctx.statusCode != null && <StatusCodeBadge status={ctx.statusCode} />}
          </div>
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">{notification.title}</h1>
          <Notification.Time absolute className="block text-sm text-muted-foreground" />
        </header>

        {/* Bloco técnico só pro admin — ver `ehDetalheTecnico`. */}
        {tecnico && (
          <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">Requisição:</span>
              <Requisicao ctx={ctx} />
            </div>
            {ctx.exceptionType && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">Exceção:</span>
                <code className="text-sm font-medium">{ctx.exceptionType}</code>
              </div>
            )}
          </div>
        )}

        {/* Corpo SEM a linha de link: ela virou o botão abaixo. */}
        <p className="whitespace-pre-wrap break-words text-base leading-relaxed text-foreground">
          {corpoSemLinhaDeLink(notification.body)}
        </p>

        <BotaoDeDetalhes errorLogId={ctx.errorLogId} />

        <Notification.Meta />

        {(showReadAction || showDeleteAction) && (
          <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-4">
            {showDeleteAction && <Notification.DeleteAction variant="labeled" onSuccess={props.onDeleted} />}
            {showReadAction && <Notification.ReadAction variant="labeled" onSuccess={props.onMarkedRead} />}
          </div>
        )}
      </article>
    </Notification>
  )
}

export const adminHttpErrorRenderer: NotificationRenderer = {
  Preview: PreviewCard,
  Full: FullView,
}
